import { Avatar as MUIAvatar } from "@mui/material";
import { INGAvatarProps } from "../../library/NGFieldExtensions";

import { useSignal, useSignalEffect } from "@preact/signals-react";
import { publishUserAction } from "../../library/analytics";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { log } from "../../library/logger";
import {
  getClassName,
  getInitials,
  getTestId,
  getsxObject,
  isNullOrEmpty,
  partitionMUIClasses,
} from "../../library/utils";
import { convertToSizedImageFilename, setupImageDetails } from "../ComponentUtils";
import { validateCookiesForUrl } from "../../library/cookieValidation";

const tag = "NGAvatar";

export default function NGAvatar({ config, context }: INGAvatarProps) {
  const local = setupLocalState(
    config,
    {
      Value: useSignal(config.Value ?? ""),
      Visible: useSignal(config.Visible ?? true),
      AlternativeText: useSignal(config.AlternativeText || ""),
      Size: useSignal(config.Size || "Medium"),
      Path: useSignal(config.Path || ""),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Source: useSignal(config.Source ?? null), // Asset, Vault, External
    },
    context
  );

  log.info(tag, `render ${local.Value.value} `, config);

  const url = useSignal<string | undefined>("");

  // Validate cookies for the avatar URL before rendering
  const cookieValidated = useSignal(false);

  useSignalEffect(() => {
    const validateAvatar = async () => {
      if (isNullOrEmpty(local.Value.value)) {
        // For default/no-image avatars, no validation needed
        cookieValidated.value = true;
        return;
      }

      try {
        await validateCookiesForUrl(local.Value.value);
        cookieValidated.value = true;
      } catch (error) {
        log.error(tag, "Cookie validation failed for avatar:", local.Value.value, error);
        cookieValidated.value = false;
      }
    };

    validateAvatar();
  });

  useSignalEffect(() => {
    if (!local.Visible.value || !cookieValidated.value) return;

    // console.log("NGAvatar-start", local.AlternativeText.value, url.value);

    if (isNullOrEmpty(local.Value.value)) {
      url.value = "/avatar/noimage.png";
      //   console.log("NGAvatar-undefined", local.AlternativeText.value, url.value);
      return;
    }
    const value = local.Value.value;

    setupImageDetails(local, value, url, convertToSizedImageFilename);

    // console.log("NGAvatar-end", local.AlternativeText.value, url.value);
  });

  const handlers = setupHandlers(config, context);

  return (
    <>
      {local.Visible.value && cookieValidated.value && url.value && (
        <MUIAvatar
          data-testid={getTestId(config)}
          sx={getsxObject(local.Style.value)}
          src={url.value && url.value.toLowerCase().includes("noimage") ? undefined : url.value}
          alt={local.AlternativeText.value as string}
          className={getClassName(local.Classes)}
          {...handlers}
          onChange={(e) => {
            const [_, classes] = partitionMUIClasses(local.Classes.value);
            const action = publishUserAction(config, context, "avatar_change", e, {
              Value: local.Value.value,
              ElementText: local.AlternativeText.value,
              EventCategory: "engagement",
              ElementClass: classes.join(" "),
            });
            handlers["onChange"]?.(e, null, action);
          }}
        >
          {getInitials(local.AlternativeText.value)}
        </MUIAvatar>
      )}
    </>
  );
}
